/**
 * DriftRow — one collapsible card per service in the drift tab.
 *
 * Layout mirrors PipelineTab's pipeline-microservice card:
 *   [▶] service-name            status-badge     compare-link
 *       from_sha → to_sha
 *
 * Expanded shows the ahead/behind timeline inside the same card.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/Badge';
import { buildTimeline, type TimelineItem } from '@/utils/driftTimeline';
import { JIRA_BROWSE_URL, GITHUB_ORG_URL, type DriftEnv } from '@/utils/driftConfig';
import type { DriftCell, DriftCommit, DriftPr, DriftSide, DriftSummaryAggregateResponse } from '@/types/api';
import { SummarisePanel, type SummaryState } from './SummarisePanel';

interface DriftRowProps {
  cell: DriftCell;
  /** Env pair drives the /drift/summary call in the expanded region. */
  from: DriftEnv;
  to: DriftEnv;
  /**
   * Rolling-30-day spend aggregate (fetched once by DriftTab, shared
   * across all rows). Null while loading or after a fetch error; the
   * cost strip and button cost hint degrade gracefully by hiding.
   */
  aggregate: DriftSummaryAggregateResponse | null;
  /**
   * Per-service summary state, lifted to DriftTab (CORE-1011) so the
   * release-notes panel's attention-needed chips can auto-trigger
   * Summarise on a target row from outside the row itself. Also survives
   * expand/collapse cycles as before (the previous local state did the
   * same, this just moves the ownership up one level).
   */
  summaryState: SummaryState;
  setSummaryState: (state: SummaryState) => void;
  /**
   * When true, this row auto-expands on mount / prop-change. Used by the
   * release-notes attention chips to open the target row without the
   * user having to click the expand button first.
   */
  autoExpand?: boolean;
}

export function DriftRow({
  cell,
  from,
  to,
  aggregate,
  summaryState,
  setSummaryState,
  autoExpand,
}: DriftRowProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(autoExpand === true);
  const rowRef = useRef<HTMLDivElement | null>(null);
  // Track when autoExpand flips true so a repeat click (chip → same service)
  // still opens the row after a manual collapse. Also scrolls the row into
  // view so a chip-targeted row that's below the fold becomes visible.
  //
  // The `scrollIntoView` guard on the method's existence protects Jest's
  // JSDOM (which doesn't implement it in older versions). Production
  // browsers always have it.
  useEffect(() => {
    if (autoExpand !== true) return;
    setExpanded(true);
    if (rowRef.current !== null && typeof rowRef.current.scrollIntoView === 'function') {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [autoExpand]);
  const detailsId = `drift-row-${cell.service}-details`;
  const accessibleLabel = describeCellStatus(cell);
  const compareUrl =
    cell.from_sha !== null && cell.to_sha !== null
      ? `${GITHUB_ORG_URL}/${cell.repo}/compare/${cell.to_sha}...${cell.from_sha}`
      : null;

  return (
    <div ref={rowRef} className={`drift-row ${expanded ? 'drift-row-expanded' : ''}`}>
      <div className="drift-row-header">
        <button
          type="button"
          className="drift-expand-btn"
          onClick={(): void => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls={detailsId}
          aria-label={accessibleLabel}
        >
          <span className="drift-expand-icon" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
        </button>
        <span className="drift-repo">{cell.service}</span>
        <div className="drift-row-right">
          {cell.from_sha !== null && cell.to_sha !== null && (
            <span className="drift-row-shas">
              <code className="drift-commit">{cell.from_sha.slice(0, 7)}</code>
              <span className="drift-arrow" aria-hidden> → </span>
              <code className="drift-commit">{cell.to_sha.slice(0, 7)}</code>
            </span>
          )}
          <div className="drift-row-summary">
            <StatusIndicator cell={cell} />
          </div>
          {compareUrl !== null && (
            <a
              className="drift-compare-link"
              href={compareUrl}
              target="_blank"
              rel="noreferrer"
            >
              Compare
            </a>
          )}
        </div>
      </div>
      {expanded && (
        <div
          id={detailsId}
          className="drift-row-details"
          role="region"
          aria-label={`${cell.service} details`}
        >
          <SummarisePanel
            from={from}
            to={to}
            service={cell.service}
            state={summaryState}
            setState={setSummaryState}
            aggregate={aggregate}
          />
          {cell.error !== null && (
            <div className="drift-row-error">{cell.error}</div>
          )}
          {cell.error === null && cell.ahead.count === 0 && cell.behind.count === 0 && (
            <div className="drift-row-empty">In sync — nothing to promote.</div>
          )}
          {cell.error === null && cell.ahead.count > 0 && (
            <Section title="Ahead" side={cell.ahead} repo={cell.repo} />
          )}
          {cell.error === null && cell.diverged && cell.behind.count > 0 && (
            <Section
              title="Behind"
              side={cell.behind}
              repo={cell.repo}
              subtitle="These commits are in the target env but not in the source — triage before next promotion."
              variant="alert"
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Human-readable status summary for screen readers.
 * Used as the aria-label on the row's expand/collapse button.
 */
function describeCellStatus(cell: DriftCell): string {
  if (cell.error !== null) {
    return `${cell.service}: error — ${cell.error}`;
  }
  if (cell.in_sync) {
    return `${cell.service}: in sync`;
  }
  if (cell.diverged) {
    return `${cell.service}: diverged, ${cell.ahead.count} ahead and ${cell.behind.count} behind`;
  }
  if (cell.ahead.count > 0) {
    return `${cell.service}: ${cell.ahead.count} ahead`;
  }
  if (cell.behind.count > 0) {
    return `${cell.service}: ${cell.behind.count} behind`;
  }
  return `${cell.service}: no changes`;
}

function StatusIndicator({ cell }: { cell: DriftCell }): JSX.Element {
  if (cell.error !== null) {
    return <Badge variant="error">✗ error</Badge>;
  }
  if (cell.in_sync) {
    return <Badge variant="success">✓ in sync</Badge>;
  }
  if (cell.diverged) {
    return (
      <Badge variant="warning">
        ⚠ diverged · {cell.ahead.count} ahead / {cell.behind.count} behind
      </Badge>
    );
  }
  const ahead = cell.ahead.count;
  const behind = cell.behind.count;
  if (ahead > 0) {
    return <Badge variant="info">↑ {ahead} ahead</Badge>;
  }
  if (behind > 0) {
    return <Badge variant="info">↓ {behind} behind</Badge>;
  }
  return <Badge variant="neutral">—</Badge>;
}

interface SectionProps {
  title: string;
  side: DriftSide;
  repo: string;
  subtitle?: string;
  variant?: 'alert';
}

function Section({ title, side, repo, subtitle, variant }: SectionProps): JSX.Element {
  const timeline = buildTimeline(side);
  return (
    <section className={`drift-section ${variant === 'alert' ? 'drift-section-alert' : ''}`}>
      <h4 className="drift-section-title">
        {title} ({side.count})
      </h4>
      {subtitle !== undefined && <p className="drift-section-subtitle">{subtitle}</p>}
      <ul className="drift-timeline">
        {timeline.map((item) => (
          <li key={itemKey(item)} className="drift-timeline-item">
            {item.kind === 'pr' ? (
              <PrCard pr={item.pr} />
            ) : (
              <CommitCard commit={item.commit} repo={repo} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function itemKey(item: TimelineItem): string {
  return item.kind === 'pr' ? `pr-${item.pr.number}` : `commit-${item.commit.sha}`;
}

function PrCard({ pr }: { pr: DriftPr }): JSX.Element {
  return (
    <div className="drift-card drift-card-pr">
      <div className="drift-card-header">
        <span className="drift-card-num">#{pr.number}</span>
        <a href={pr.url} target="_blank" rel="noreferrer" className="drift-card-title">
          {pr.title}
        </a>
      </div>
      <div className="drift-card-meta">
        <span>{pr.author}</span>
        <span> · </span>
        <time dateTime={pr.merged_at}>{pr.merged_at}</time>
      </div>
      <TicketBadges tickets={pr.tickets} />
    </div>
  );
}

function CommitCard({ commit, repo }: { commit: DriftCommit; repo: string }): JSX.Element {
  const commitUrl = `${GITHUB_ORG_URL}/${repo}/commit/${commit.sha}`;
  return (
    <div className="drift-card drift-card-commit">
      <div className="drift-card-header">
        <a href={commitUrl} target="_blank" rel="noreferrer" className="drift-card-sha">
          <code>{commit.short_sha}</code>
        </a>
        <span className="drift-card-title">{commit.message}</span>
        <span className="drift-card-orphan-label">direct push</span>
      </div>
      <div className="drift-card-meta">
        <span>{commit.author}</span>
        <span> · </span>
        <time dateTime={commit.authored_at}>{commit.authored_at}</time>
      </div>
      <TicketBadges tickets={commit.tickets} />
    </div>
  );
}

function TicketBadges({ tickets }: { tickets: readonly string[] }): JSX.Element | null {
  if (tickets.length === 0) return null;
  return (
    <div className="drift-card-tickets">
      {tickets.map((t) => (
        <a
          key={t}
          href={`${JIRA_BROWSE_URL}/${t}`}
          target="_blank"
          rel="noreferrer"
          className="drift-ticket-badge"
        >
          {t}
        </a>
      ))}
    </div>
  );
}
